/** Pure helpers shared by the free-tool forms and their WebMCP tools (kept out of component files for Fast Refresh). */
import { CURRENCY_LIST, isCountryCode } from '@borderbot/core/countries';
import { parseAmount } from '@borderbot/core/format';
import type {
  ClassificationResult,
  LandedCostResult,
  OriginInference,
} from '@borderbot/core/types';
import type { FreeCalculateRequest, FreeOriginRequest } from '@/server/free-tools.functions';

export type CalculateInput = FreeCalculateRequest['input'];
export type CalculatorField =
  'product' | 'origin' | 'destination' | 'value' | 'shipping' | 'quantity' | 'weight';
export type CalculatorProblem = { message: string; field: CalculatorField };

export type OriginInput = FreeOriginRequest['input'];
export type OriginField = 'product' | 'url' | 'gtin';
export type OriginProblem = { message: string; field: OriginField };

const CURRENCY_CODES = new Set(CURRENCY_LIST.map((currency) => currency.code));

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Compact, agent-friendly summary of a classification. */
export function summariseClassification(result: ClassificationResult, remaining: number | null) {
  return {
    hsCode: result.hsCode,
    hsCodeFormatted: result.hsCodeFormatted,
    nomenclature: result.nomenclature,
    destinationCountry: result.destinationCountry,
    description: result.description,
    confidence: result.confidence,
    needsReview: result.needsReview,
    hierarchy: result.hierarchy,
    reasoning: result.reasoning,
    schedule: result.schedule ?? null,
    tariffData: result.dataSource?.version ?? null,
    // Facts the code rests on that nobody stated: ask the person before relying on it.
    materialUncertain: result.materialUncertain ?? false,
    valueUncertain: result.valueUncertain ?? false,
    // What a web search said the product is, with the pages it used (cite them with it).
    identifiedAs: result.identifiedAs ?? null,
    webSources: result.identifiedAs ? (result.webSources ?? []) : [],
    alternatives: result.alternatives.slice(0, 3).map((alternative) => ({
      hsCode: alternative.hsCodeFormatted || alternative.hsCode,
      description: alternative.description,
      confidence: alternative.confidence,
    })),
    detectedOrigin: result.origin?.country
      ? {
          country: result.origin.country,
          source: result.origin.source,
          evidence: result.origin.evidence,
        }
      : null,
    freeRunsRemaining: remaining,
  };
}

/** Amounts from the form or a WebMCP agent; "12,5" is 12.5, not 125 (see `parseAmount`). */
function parseNumber(value: unknown): number | undefined {
  return parseAmount(value);
}

/** Why the goods are sent, as the calculator offers it. A gift is a personal one: a free gift with an order is a sale. */
export const CALCULATOR_PURPOSES = [
  { value: 'sale', label: 'Sold to the buyer' },
  { value: 'gift', label: 'A personal gift from a person abroad, not bought from a store' },
] as const;

/** Who sends a gift. */
export const GIFT_SENDERS = [
  { value: 'person', label: 'A person' },
  { value: 'business', label: 'A business' },
] as const;

/**
 * What a gift is allowed into the destination free of, in plain words, when the calculator knows it (US 19 CFR 10.152
 * and 10.153); null elsewhere, where the result's notes say what applies.
 */
export function giftAllowanceNote(
  destination: string,
  sender: 'person' | 'business',
): string | null {
  if (destination !== 'US') return null;
  return sender === 'person'
    ? 'The US lets in gifts from a person abroad free of duty and tax up to $100 in total per recipient per day ($200 from the US Virgin Islands, Guam or American Samoa). Anything bought, or given with a purchase, isn’t a gift.'
    : 'In the US the gift allowance is for gifts from a person. A business’s gift only qualifies when the parcel is clearly marked as a gift and a statement is enclosed; otherwise duty is charged as on a sale.';
}

/** Validate and normalise calculator input (shared by the form and the WebMCP tool). */
export function buildCalculateInput(raw: {
  hsCode?: unknown;
  description?: unknown;
  originCountry?: unknown;
  destinationCountry?: unknown;
  value?: unknown;
  currency?: unknown;
  shippingCost?: unknown;
  quantity?: unknown;
  weight?: unknown;
  weightUnit?: unknown;
  purpose?: unknown;
  sender?: unknown;
}): CalculateInput | CalculatorProblem {
  const hsCode = typeof raw.hsCode === 'string' ? raw.hsCode.replace(/[.\s-]/g, '') : '';
  const description = typeof raw.description === 'string' ? raw.description.trim() : '';
  const originCountry =
    typeof raw.originCountry === 'string' ? raw.originCountry.trim().toUpperCase() : '';
  const destinationCountry =
    typeof raw.destinationCountry === 'string' ? raw.destinationCountry.trim().toUpperCase() : '';
  const value = parseNumber(raw.value);
  const shippingCost = parseNumber(raw.shippingCost);
  const quantity = parseNumber(raw.quantity) ?? 1;
  const weight = parseNumber(raw.weight);
  const currency =
    typeof raw.currency === 'string' && raw.currency.trim()
      ? raw.currency.trim().toUpperCase()
      : 'USD';
  const weightUnit = raw.weightUnit === 'lb' ? 'lb' : 'kg';
  const gift = raw.purpose === 'gift';
  const sender =
    raw.sender === 'business' ? 'business' : raw.sender === 'person' ? 'person' : undefined;

  if (!hsCode && !description)
    return { message: 'Enter an HS code, or describe the product.', field: 'product' };
  if (hsCode && !/^\d{4,10}$/.test(hsCode))
    return { message: 'HS codes have 4 to 10 digits.', field: 'product' };
  if (!hsCode && description.length < 2)
    return { message: 'Add a little more detail to the description.', field: 'product' };
  if (!isCountryCode(originCountry))
    return { message: 'Choose the country of origin.', field: 'origin' };
  if (!isCountryCode(destinationCountry))
    return { message: 'Choose the destination country.', field: 'destination' };
  if (value === undefined || value <= 0 || value > 1_000_000) {
    return {
      message: 'Enter the total goods value of the shipment (more than 0 and up to 1,000,000).',
      field: 'value',
    };
  }
  if (shippingCost !== undefined && (shippingCost < 0 || shippingCost > 1_000_000)) {
    return { message: 'Shipping cost must be between 0 and 1,000,000.', field: 'shipping' };
  }
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100_000) {
    return { message: 'Units must be a whole number from 1 to 100,000.', field: 'quantity' };
  }
  if (weight !== undefined && (weight <= 0 || weight > 100_000)) {
    return { message: 'Weight must be more than 0 and up to 100,000.', field: 'weight' };
  }
  return {
    hsCode: hsCode || undefined,
    description: hsCode ? undefined : description,
    originCountry,
    destinationCountry,
    value,
    currency: CURRENCY_CODES.has(currency) ? currency : 'USD',
    shippingCost,
    quantity,
    weight,
    weightUnit: weight !== undefined ? weightUnit : undefined,
    // Sales are the default; who sends the goods only matters for a gift.
    ...(gift ? { purpose: 'gift' as const, ...(sender ? { sender } : {}) } : {}),
  };
}

export function summariseLandedCost(
  result: LandedCostResult,
  remaining: number | null,
  classifiedAs: string | null,
) {
  return {
    hsCode: result.hsCodeFormatted || result.hsCode,
    classifiedFromDescription: classifiedAs,
    originCountry: result.originCountry,
    destinationCountry: result.destinationCountry,
    currency: result.currency,
    totals: result.totals,
    lines: result.lines.map((line) => ({
      kind: line.kind,
      name: line.name,
      rate: line.rate,
      amount: line.amount,
    })),
    deMinimis: result.deMinimis,
    rateFound: result.rateFound ?? null,
    availableExemptions: result.availableExemptions ?? [],
    conditionalMeasuresNotInTotals: result.regulatory.map((flag) => ({
      name: flag.name,
      agency: flag.agency,
    })),
    tariffData: result.dataSource?.version ?? null,
    notes: result.notes,
    freeRunsRemaining: remaining,
  };
}

const text = (value: unknown, max: number) =>
  typeof value === 'string' ? value.trim().slice(0, max) : '';

/** Validate and normalise origin-finder input (shared by the form and the WebMCP tool). */
export function buildOriginInput(raw: {
  productUrl?: unknown;
  title?: unknown;
  description?: unknown;
  brand?: unknown;
  material?: unknown;
  gtin?: unknown;
  shipFromCountry?: unknown;
}): OriginInput | OriginProblem {
  const productUrl = text(raw.productUrl, 2048);
  const title = text(raw.title, 300);
  const description = text(raw.description, 2000);
  const brand = text(raw.brand, 120);
  const material = text(raw.material, 300);
  // Barcodes are often written with spaces or hyphens between digit groups.
  const gtin = text(raw.gtin, 40).replace(/[\s-]/g, '');
  const shipFromCountry = text(raw.shipFromCountry, 2).toUpperCase();

  if (!productUrl && !title && !description && !gtin) {
    return {
      message: 'Paste a product page URL, or give the product’s title, description or barcode.',
      field: 'product',
    };
  }
  if (productUrl && !isHttpUrl(productUrl)) {
    return { message: 'Enter a full product URL starting with https://', field: 'url' };
  }
  if (gtin && !/^\d{8,14}$/.test(gtin)) {
    return { message: 'A barcode (GTIN, UPC or EAN) is 8 to 14 digits.', field: 'gtin' };
  }
  return {
    productUrl: productUrl || undefined,
    title: title || undefined,
    description: description || undefined,
    brand: brand || undefined,
    material: material || undefined,
    gtin: gtin || undefined,
    shipFromCountry: isCountryCode(shipFromCountry) ? shipFromCountry : undefined,
  };
}

/** Compact, agent-friendly summary of an origin inference. */
export function summariseOrigin(result: OriginInference, remaining: number | null) {
  return {
    country: result.country,
    probability: result.probability ?? null,
    confidence: result.confidence,
    source: result.source,
    evidence: result.evidence,
    // A person should confirm the origin before it is declared; say why.
    needsReview: result.needsReview ?? false,
    reviewReason: result.reviewReason ?? null,
    alternates: (result.alternates ?? []).slice(0, 5),
    signals: (result.signals ?? []).map((signal) => ({
      kind: signal.kind,
      country: signal.country,
      weight: signal.weight,
      evidence: signal.evidence,
    })),
    reasoning: result.reasoning ?? null,
    freeRunsRemaining: remaining,
  };
}
